import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '@/lib/api';

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user, token } = useAuth();
  const [driverLocation, setDriverLocation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeBooking, setActiveBooking] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const watchIdRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Start tracking driver location (for drivers)
  const startDriverTracking = useCallback((bookingId) => {
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return;
    }

    setIsTracking(true);
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          timestamp: new Date().toISOString()
        };
        
        setDriverLocation(location);
        
        // Send to server (use explicit bookingId or fall back to activeBooking)
        const targetBookingId = bookingId || activeBooking?.id;
        if (token && targetBookingId) {
          try {
            await fetch(`${getApiUrl()}/api/driver/location`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                booking_id: targetBookingId,
                ...location
              })
            });
          } catch (err) {
            console.error('Failed to send location:', err);
          }
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000
      }
    );
  }, [token, activeBooking]);

  // Stop tracking
  const stopDriverTracking = useCallback(() => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
  }, []);

  // Poll for driver location (for customers)
  const startCustomerTracking = useCallback((bookingId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const fetchLocation = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/tracking/${bookingId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setDriverLocation(data.location);
        }
      } catch (err) {
        console.error('Failed to fetch driver location:', err);
      }
    };

    fetchLocation();
    pollIntervalRef.current = setInterval(fetchLocation, 5000); // Every 5 seconds
  }, [token]);

  const stopCustomerTracking = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setDriverLocation(null);
  }, []);

  // Send message. Optionally attach a photo_url from a prior /messages/upload-photo call.
  const sendMessage = useCallback(async (bookingId, text, photoUrl = null) => {
    // Allow photo-only messages (text may be empty)
    if (!text?.trim() && !photoUrl) return null;

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${getApiUrl()}/api/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          booking_id: bookingId,
          text: (text || '').trim(),
          photo_url: photoUrl || undefined,
        })
      });

      if (response.ok) {
        const message = await response.json();
        setMessages(prev => [...prev, message]);
        return message;
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
    return null;
  }, [token]);

  // Fetch messages
  const fetchMessages = useCallback(async (bookingId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/messages/${bookingId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [token]);

  // Poll for new messages
  useEffect(() => {
    if (!activeBooking) return;

    fetchMessages(activeBooking.id);
    const interval = setInterval(() => fetchMessages(activeBooking.id), 3000);
    
    return () => clearInterval(interval);
  }, [activeBooking, fetchMessages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDriverTracking();
      stopCustomerTracking();
    };
  }, [stopDriverTracking, stopCustomerTracking]);

  return (
    <RealtimeContext.Provider value={{
      driverLocation,
      messages,
      activeBooking,
      isTracking,
      setActiveBooking,
      startDriverTracking,
      stopDriverTracking,
      startCustomerTracking,
      stopCustomerTracking,
      sendMessage,
      fetchMessages
    }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};
